import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import ContentArea from '../../components/contentArea';
import Navigation from '../../components/navigation';
import Button from '../../components/button';
import Toggle from '../../components/toggle';
import { ContentsIconDefault } from '../../assets/images/contentsIconDefault';
import { contentAPI } from '../../actions/imsServer';
import { CONTENT_TYPES, API_PAGE_SIZE } from '../../constants';
import { getLocalizedName } from '../../utils/base';

export const ContentSelection = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    // 이전 페이지에서 전달받은 폼 데이터
    const formData = location.state?.formData || {};

    // 상태 관리
    const [selectedCategory, setSelectedCategory] = useState(0); // 0: 체력단련, 1: 두뇌훈련, 2: 헬스게임
    const [selectedContent, setSelectedContent] = useState(null);
    const [contentData, setContentData] = useState({ 0: [], 1: [], 2: [], 3: [] });
    const [loading, setLoading] = useState(true);

    // 카테고리 옵션
    const categoryOptions = CONTENT_TYPES.map(type => t(type.label));

    // 콘텐츠 데이터 로드
    useEffect(() => {
        const loadContents = async () => {
            try {
                setLoading(true);
                const allContents = [];
                const limit = API_PAGE_SIZE;
                let offset = 0;
                let hasMore = true;

                while (hasMore) {
                    const response = await contentAPI.getContents({ state: 0, limit, offset });
                    const list = response?.data?.contents ?? response?.contents ?? [];
                    const arr = Array.isArray(list) ? list : [];
                    allContents.push(...arr);
                    if (arr.length < limit) {
                        hasMore = false;
                    } else {
                        offset += limit;
                    }
                }

                // championship이 1인 콘텐츠만 필터링
                const championshipContents = allContents.filter(content => content.championship === 1);

                // 타입별로 분류 (type: 0=체력단련, 1=두뇌훈련, 2=헬스게임)
                const categorized = {
                    0: [], // 체력단련
                    1: [], // 두뇌훈련
                    2: [], // 헬스게임                   
                };

                championshipContents.forEach(content => {
                    const type = content.type !== undefined ? content.type : 0;
                    const categoryIndex = type >= 0 && type <= 3 ? type : 0;

                    categorized[categoryIndex].push({
                        id: content.id || content.index,
                        index: content.index,
                        name: getLocalizedName(content, i18n.language) || '-',
                        icon: content.icon || <ContentsIconDefault />
                    });
                });

                setContentData(categorized);
            } catch (err) {
                console.error('콘텐츠 로딩 오류:', err);
                setContentData({ 0: [], 1: [], 2: [] });
            } finally {
                setLoading(false);
            }
        };

        loadContents();
    }, [i18n.language]);

    // 카테고리 변경 핸들러
    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
        setSelectedContent(null); // 카테고리 변경 시 선택된 종목 초기화
    };

    // 종목 선택 핸들러
    const handleContentSelect = (content) => {
        setSelectedContent(content);
    };

    // 취소 버튼 클릭
    const handleCancel = () => {
        navigate(-1);
    };

    // 확인 버튼 클릭
    const handleConfirm = () => {
        if (selectedContent) {
            // 선택된 종목을 폼 데이터에 추가하고 이전 페이지로 돌아가기
            const updatedFormData = {
                ...formData,
                contentName: selectedContent.name,
                contentIndex: selectedContent.index
            };

            navigate('/championships/user-add', { state: { formData: updatedFormData } });
        }
    };

    const currentContents = contentData[selectedCategory] || [];

    return (
        <ContentArea sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{
                width: '100%',
                padding: '0 20px',
                flexShrink: 0,
                zIndex: 1
            }}>
                <Navigation title={t('championship_event_selection')} showLeftIcon={false} />
            </Box>

            {/* Main Content */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                padding: '0 10px',
                gap: '60px',
                zIndex: 1,
                width: '100%'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    alignItems: 'center',
                }}>
                    {/* 카테고리 토글 */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        alignItems: 'flex-start',
                    }}>
                        <Toggle
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                        />
                    </Box>

                    {/* 종목 그리드 */}
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        {loading ? (
                            <Typography sx={{ color: theme.palette.figma.white[60] }}>
                                로딩 중...
                            </Typography>
                        ) : currentContents.length === 0 ? (
                            <Typography sx={{ color: theme.palette.figma.white[60] }}>
                                선택 가능한 종목이 없습니다.
                            </Typography>
                        ) : (
                            currentContents.map((content) => {
                                const isSelected = selectedContent?.id === content.id;
                                return (
                                    <Box
                                        key={content.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '5px',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                        onClick={() => handleContentSelect(content)}
                                    >
                                        {/* 아이콘 */}
                                        <Box sx={{
                                            width: '70px',
                                            height: '70px',
                                            borderRadius: '72px',
                                            border: isSelected ? '4px solid ' + theme.palette.figma.buttonOrange : '2px solid white',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            position: 'relative',
                                            opacity: isSelected ? 1 : 0.6,
                                            '& img': {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            },
                                        }}>
                                            {content.icon ? (
                                                <img src={content.icon} alt={content.name} />
                                            ) : <ContentsIconDefault />}
                                        </Box>

                                        {/* 종목명 */}
                                        <Typography sx={{
                                            fontSize: '12px',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected
                                                ? theme.palette.figma.white[80]
                                                : theme.palette.figma.white[60],
                                            textAlign: 'center',
                                            letterSpacing: '-0.36px',
                                            lineHeight: 'normal'
                                        }}>
                                            {content.name}
                                        </Typography>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Bottom Buttons */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                gap: '10px',
                padding: '0 20px 54px',
                flexShrink: 0,
                zIndex: 1
            }}>
                <Button
                    variant="gray"
                    onClick={handleCancel}
                    size="large"
                    sx={{ flex: 1 }}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant="normal"
                    onClick={handleConfirm}
                    size="large"
                    sx={{ flex: 1 }}
                    disabled={!selectedContent}
                >
                    {t('confirm')}
                </Button>
            </Box>
        </ContentArea>
    );
};

export default ContentSelection;
